import React, { useState, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
} from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import EmailIcon from '@mui/icons-material/Email'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../contexts/AuthContext'
import { getUsers } from '../services/users'
import {
  Account,
  getAccounts,
  createAccount,
  updateAccount,
  deleteAccount,
  transferOwnership,
  addUserToAccount,
  inviteUserToAccount,
  migrateAccounts,
} from '../services/accounts'
import { User } from '../types'

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid ${props => props.theme.colors.border};
`

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize.xxxlarge};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text};
  margin: 0;
`

function AccountRow({
  account,
  childAccounts,
  onView,
  onEdit,
  onDelete,
  onAddUser,
  onInvite,
  onTransfer,
  t,
}: {
  account: Account
  childAccounts: Account[]
  onView: (id: string) => void
  onEdit: (account: Account) => void
  onDelete: (account: Account) => void
  onAddUser: (account: Account) => void
  onInvite: (account: Account) => void
  onTransfer: (account: Account) => void
  t: (key: string, options?: any) => string
}): React.JSX.Element {
  const [open, setOpen] = useState(false)

  return (
    <>
      <TableRow hover>
        <TableCell>
          {childAccounts.length > 0 && (
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          <Typography variant="body1" fontWeight={600}>{account.name}</Typography>
        </TableCell>
        <TableCell>
          <Typography variant="body2">{account.ownerName}</Typography>
          <Typography variant="caption" color="text.secondary">{account.ownerEmail}</Typography>
        </TableCell>
        <TableCell align="center">
          <Chip label={account.usersCount || 0} size="small" />
        </TableCell>
        <TableCell align="center">
          <Chip label={childAccounts.length} size="small" variant="outlined" />
        </TableCell>
        <TableCell>
          <Chip
            label={account.isActive ? t('users.active') : t('users.inactive')}
            size="small"
            color={account.isActive ? 'success' : 'default'}
          />
        </TableCell>
        <TableCell align="right">
          <Tooltip title={t('accounts.inviteUser')}>
            <IconButton size="small" color="secondary" onClick={() => onInvite(account)}>
              <EmailIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('accounts.addUser')}>
            <IconButton size="small" color="primary" onClick={() => onAddUser(account)}>
              <PersonAddIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('accounts.transferOwnership')}>
            <IconButton size="small" onClick={() => onTransfer(account)}>
              <SwapHorizIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('accounts.editAccount')}>
            <IconButton size="small" color="primary" onClick={() => onEdit(account)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('accounts.deleteAccount')}>
            <IconButton size="small" color="error" onClick={() => onDelete(account)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
      {childAccounts.length > 0 && (
        <TableRow>
          <TableCell colSpan={7} sx={{ py: 0, px: 0 }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ pl: 6, pr: 2, py: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('accounts.name')}</TableCell>
                      <TableCell>{t('accounts.owner')}</TableCell>
                      <TableCell align="center">{t('accounts.usersCount')}</TableCell>
                      <TableCell align="right">{t('common.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {childAccounts.map(child => (
                      <TableRow key={child.id} hover>
                        <TableCell>{child.name}</TableCell>
                        <TableCell>{child.ownerName}</TableCell>
                        <TableCell align="center">{child.usersCount || 0}</TableCell>
                        <TableCell align="right">
                          <Tooltip title={t('accounts.inviteUser')}>
                            <IconButton size="small" color="secondary" onClick={() => onInvite(child)}>
                              <EmailIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('accounts.addUser')}>
                            <IconButton size="small" color="primary" onClick={() => onAddUser(child)}>
                              <PersonAddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={t('accounts.editAccount')}>
                            <IconButton size="small" color="primary" onClick={() => onEdit(child)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

function AccountsScreen(): React.JSX.Element {
  const { t } = useTranslation()
  const { user: authUser } = useAuth()
  const [accounts, setAccounts] = useState<Account[]>([])
  const [allUsers, setAllUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  })

  // Create account dialog
  const [createOpen, setCreateOpen] = useState(false)
  const [createMode, setCreateMode] = useState<'new' | 'existing'>('new')
  const [createForm, setCreateForm] = useState({
    name: '', address: '', ownerName: '', ownerEmail: '', ownerPassword: '', existingOwnerId: '', parentAccountId: '',
  })

  // Edit account dialog
  const [editOpen, setEditOpen] = useState(false)
  const [editAccount, setEditAccount] = useState<Account | null>(null)
  const [editName, setEditName] = useState('')
  const [editAddress, setEditAddress] = useState('')

  // Delete dialog
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deletingAccount, setDeletingAccount] = useState<Account | null>(null)

  // Add user dialog
  const [addUserOpen, setAddUserOpen] = useState(false)
  const [addUserAccount, setAddUserAccount] = useState<Account | null>(null)
  const [addUserForm, setAddUserForm] = useState({ name: '', email: '', password: '', role: 'user' })
  const [addUserSendInvitation, setAddUserSendInvitation] = useState(true)

  // Invite user dialog
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteAccount, setInviteAccount] = useState<Account | null>(null)
  const [inviteForm, setInviteForm] = useState({ email: '', name: '', role: 'user' })

  // Create account invitation toggle
  const [createSendInvitation, setCreateSendInvitation] = useState(true)

  // Transfer ownership dialog
  const [transferOpen, setTransferOpen] = useState(false)
  const [transferAccount, setTransferAccount] = useState<Account | null>(null)
  const [transferUserId, setTransferUserId] = useState('')

  const [saving, setSaving] = useState(false)

  const loadData = useCallback(async (): Promise<void> => {
    try {
      setLoading(true)
      const [accountsRes, usersRes] = await Promise.all([getAccounts(), getUsers()])
      setAccounts(accountsRes.data)
      setAllUsers(usersRes.data)
    } catch (error) {
      console.error('Error loading accounts data:', error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadData() }, [loadData])

  // Top-level accounts (no parent)
  const topAccounts = accounts.filter(a => !a.parentAccountId)
  const childMap = new Map<string, Account[]>()
  accounts.forEach(a => {
    if (a.parentAccountId) {
      const list = childMap.get(a.parentAccountId) || []
      list.push(a)
      childMap.set(a.parentAccountId, list)
    }
  })

  // Users in a specific account (for transfer dialog)
  const accountUsers = transferAccount
    ? allUsers.filter(u => u.accountId === transferAccount.id)
    : []

  async function handleCreateAccount(): Promise<void> {
    setSaving(true)
    try {
      const payload: any = { name: createForm.name }
      if (createForm.address) payload.address = createForm.address
      if (createForm.parentAccountId) payload.parentAccountId = createForm.parentAccountId
      if (createMode === 'existing') {
        payload.existingOwnerId = createForm.existingOwnerId
      } else {
        payload.ownerName = createForm.ownerName
        payload.ownerEmail = createForm.ownerEmail
        if (createSendInvitation) {
          payload.sendInvitation = true
        } else {
          payload.ownerPassword = createForm.ownerPassword
        }
      }
      await createAccount(payload)
      setCreateOpen(false)
      setCreateForm({ name: '', address: '', ownerName: '', ownerEmail: '', ownerPassword: '', existingOwnerId: '', parentAccountId: '' })
      setCreateSendInvitation(true)
      const msg = createSendInvitation && createMode === 'new'
        ? t('accounts.createSuccess') + ' ' + t('accounts.invitationSent')
        : t('accounts.createSuccess')
      setSnackbar({ open: true, message: msg, severity: 'success' })
      await loadData()
    } catch (error: any) {
      const msg = error?.response?.data?.error || t('common.error')
      setSnackbar({ open: true, message: msg, severity: 'error' })
    } finally {
      setSaving(false)
    }
  }

  async function handleEditAccount(): Promise<void> {
    if (!editAccount) return
    setSaving(true)
    try {
      await updateAccount(editAccount.id, { name: editName, address: editAddress })
      setEditOpen(false)
      setSnackbar({ open: true, message: t('accounts.updateSuccess'), severity: 'success' })
      await loadData()
    } catch (error: any) {
      setSnackbar({ open: true, message: error?.response?.data?.error || t('common.error'), severity: 'error' })
    } finally {
      setSaving(false)
    }
  }

  async function handleDeleteAccount(): Promise<void> {
    if (!deletingAccount) return
    setSaving(true)
    try {
      await deleteAccount(deletingAccount.id)
      setDeleteOpen(false)
      setSnackbar({ open: true, message: t('accounts.deleteSuccess'), severity: 'success' })
      await loadData()
    } catch (error: any) {
      setSnackbar({ open: true, message: error?.response?.data?.error || t('common.error'), severity: 'error' })
    } finally {
      setSaving(false)
    }
  }

  async function handleAddUser(): Promise<void> {
    if (!addUserAccount) return
    setSaving(true)
    try {
      if (addUserSendInvitation) {
        await addUserToAccount(addUserAccount.id, {
          name: addUserForm.name,
          email: addUserForm.email,
          role: addUserForm.role,
          sendInvitation: true,
        })
      } else {
        await addUserToAccount(addUserAccount.id, addUserForm)
      }
      setAddUserOpen(false)
      setAddUserForm({ name: '', email: '', password: '', role: 'user' })
      setAddUserSendInvitation(true)
      const msg = addUserSendInvitation
        ? t('accounts.invitationSent')
        : t('accounts.userAdded')
      setSnackbar({ open: true, message: msg, severity: 'success' })
      await loadData()
    } catch (error: any) {
      setSnackbar({ open: true, message: error?.response?.data?.error || t('common.error'), severity: 'error' })
    } finally {
      setSaving(false)
    }
  }

  async function handleInviteUser(): Promise<void> {
    if (!inviteAccount) return
    setSaving(true)
    try {
      await inviteUserToAccount(inviteAccount.id, inviteForm)
      setInviteOpen(false)
      setInviteForm({ email: '', name: '', role: 'user' })
      setSnackbar({ open: true, message: t('accounts.invitationSent'), severity: 'success' })
      await loadData()
    } catch (error: any) {
      setSnackbar({ open: true, message: error?.response?.data?.error || t('common.error'), severity: 'error' })
    } finally {
      setSaving(false)
    }
  }

  async function handleTransfer(): Promise<void> {
    if (!transferAccount || !transferUserId) return
    setSaving(true)
    try {
      await transferOwnership(transferAccount.id, transferUserId)
      setTransferOpen(false)
      setTransferUserId('')
      setSnackbar({ open: true, message: t('accounts.transferSuccess'), severity: 'success' })
      await loadData()
    } catch (error: any) {
      setSnackbar({ open: true, message: error?.response?.data?.error || t('common.error'), severity: 'error' })
    } finally {
      setSaving(false)
    }
  }

  async function handleMigrate(): Promise<void> {
    setSaving(true)
    try {
      const res = await migrateAccounts()
      setSnackbar({ open: true, message: t('accounts.migrationComplete', { count: Number((res.data as any).accountsCreated) || 0 }), severity: 'success' })
      await loadData()
    } catch (error: any) {
      setSnackbar({ open: true, message: error?.response?.data?.error || t('common.error'), severity: 'error' })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return <Container><Typography>{t('common.loading')}</Typography></Container>
  }

  return (
    <Container>
      <Header>
        <Title>{t('accounts.title')}</Title>
        <Box display="flex" gap={1}>
          {authUser?.role === 'super_admin' && accounts.length === 0 && (
            <Button variant="outlined" onClick={handleMigrate} disabled={saving}>
              {t('accounts.migrateExisting')}
            </Button>
          )}
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            {t('accounts.createAccount')}
          </Button>
        </Box>
      </Header>

      {topAccounts.length === 0 ? (
        <Typography color="text.secondary">{t('accounts.noAccounts')}</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 50 }} />
                <TableCell>{t('accounts.name')}</TableCell>
                <TableCell>{t('accounts.owner')}</TableCell>
                <TableCell align="center">{t('accounts.usersCount')}</TableCell>
                <TableCell align="center">{t('accounts.subAccounts')}</TableCell>
                <TableCell>{t('users.status')}</TableCell>
                <TableCell align="right">{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {topAccounts.map(account => (
                <AccountRow
                  key={account.id}
                  account={account}
                  childAccounts={childMap.get(account.id) || []}
                  onView={(id) => {}}
                  onEdit={(a) => { setEditAccount(a); setEditName(a.name); setEditAddress(a.address || ''); setEditOpen(true) }}
                  onDelete={(a) => { setDeletingAccount(a); setDeleteOpen(true) }}
                  onAddUser={(a) => { setAddUserAccount(a); setAddUserSendInvitation(true); setAddUserOpen(true) }}
                  onInvite={(a) => { setInviteAccount(a); setInviteOpen(true) }}
                  onTransfer={(a) => { setTransferAccount(a); setTransferOpen(true) }}
                  t={t}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create Account Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('accounts.createAccount')}</DialogTitle>
        <DialogContent>
          <Box display="grid" gap={2} mt={1}>
            <TextField
              label={t('accounts.accountName')}
              value={createForm.name}
              onChange={e => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              autoFocus
            />
            <TextField
              label={t('accounts.address')}
              value={createForm.address}
              onChange={e => setCreateForm(prev => ({ ...prev, address: e.target.value }))}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>{t('accounts.parentAccount')}</InputLabel>
              <Select
                value={createForm.parentAccountId}
                label={t('accounts.parentAccount')}
                onChange={e => setCreateForm(prev => ({ ...prev, parentAccountId: e.target.value }))}
              >
                <MenuItem value="">{t('accounts.noParent')}</MenuItem>
                {accounts.map(a => (
                  <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tabs value={createMode} onChange={(_, v) => setCreateMode(v)} sx={{ minHeight: 36 }}>
              <Tab value="new" label={t('accounts.newOwner')} sx={{ minHeight: 36, textTransform: 'none' }} />
              <Tab value="existing" label={t('accounts.existingOwner')} sx={{ minHeight: 36, textTransform: 'none' }} />
            </Tabs>
            {createMode === 'new' ? (
              <>
                <TextField
                  label={t('users.name')}
                  value={createForm.ownerName}
                  onChange={e => setCreateForm(prev => ({ ...prev, ownerName: e.target.value }))}
                  fullWidth
                />
                <TextField
                  label={t('users.email')}
                  value={createForm.ownerEmail}
                  onChange={e => setCreateForm(prev => ({ ...prev, ownerEmail: e.target.value }))}
                  fullWidth
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={createSendInvitation}
                      onChange={e => setCreateSendInvitation(e.target.checked)}
                    />
                  }
                  label={t('accounts.sendInvitation')}
                />
                {createSendInvitation ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('accounts.invitationHelp')}
                  </Typography>
                ) : (
                  <TextField
                    label={t('users.password')}
                    type="password"
                    value={createForm.ownerPassword}
                    onChange={e => setCreateForm(prev => ({ ...prev, ownerPassword: e.target.value }))}
                    fullWidth
                  />
                )}
              </>
            ) : (
              <FormControl fullWidth>
                <InputLabel>{t('accounts.selectOwner')}</InputLabel>
                <Select
                  value={createForm.existingOwnerId}
                  label={t('accounts.selectOwner')}
                  onChange={e => setCreateForm(prev => ({ ...prev, existingOwnerId: e.target.value }))}
                >
                  {allUsers.map(u => (
                    <MenuItem key={u.id} value={u.id}>{u.name} ({u.email})</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={saving}>{t('button.cancel.text')}</Button>
          <Button
            variant="contained"
            onClick={handleCreateAccount}
            disabled={saving || !createForm.name || (createMode === 'new' ? (!createForm.ownerName || !createForm.ownerEmail || (!createSendInvitation && !createForm.ownerPassword)) : !createForm.existingOwnerId)}
          >
            {saving ? t('button.save.loading') : t('button.save.text')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Account Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('accounts.editAccount')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('accounts.accountName')}
            fullWidth
            value={editName}
            onChange={e => setEditName(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t('accounts.address')}
            fullWidth
            value={editAddress}
            onChange={e => setEditAddress(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={saving}>{t('button.cancel.text')}</Button>
          <Button variant="contained" onClick={handleEditAccount} disabled={saving || !editName}>
            {saving ? t('button.save.loading') : t('button.save.text')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Account Dialog */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>{t('accounts.deleteAccount')}</DialogTitle>
        <DialogContent>
          <Typography>{t('accounts.confirmDelete', { name: deletingAccount?.name })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={saving}>{t('button.cancel.text')}</Button>
          <Button color="error" variant="contained" onClick={handleDeleteAccount} disabled={saving}>
            {saving ? t('common.loading') : t('button.delete.text')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add User to Account Dialog */}
      <Dialog open={addUserOpen} onClose={() => setAddUserOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('accounts.addUser')} - {addUserAccount?.name}</DialogTitle>
        <DialogContent>
          <Box display="grid" gap={2} mt={1}>
            <TextField
              label={t('users.name')}
              value={addUserForm.name}
              onChange={e => setAddUserForm(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
            />
            <TextField
              label={t('users.email')}
              value={addUserForm.email}
              onChange={e => setAddUserForm(prev => ({ ...prev, email: e.target.value }))}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={addUserSendInvitation}
                  onChange={e => setAddUserSendInvitation(e.target.checked)}
                />
              }
              label={t('accounts.sendInvitation')}
            />
            {addUserSendInvitation ? (
              <Typography variant="body2" color="text.secondary">
                {t('accounts.invitationHelp')}
              </Typography>
            ) : (
              <TextField
                label={t('users.password')}
                type="password"
                value={addUserForm.password}
                onChange={e => setAddUserForm(prev => ({ ...prev, password: e.target.value }))}
                fullWidth
              />
            )}
            <FormControl fullWidth>
              <InputLabel>{t('users.role')}</InputLabel>
              <Select
                value={addUserForm.role}
                label={t('users.role')}
                onChange={e => setAddUserForm(prev => ({ ...prev, role: e.target.value }))}
              >
                <MenuItem value="admin">{t('users.roles.admin')}</MenuItem>
                <MenuItem value="user">{t('users.roles.user')}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddUserOpen(false)} disabled={saving}>{t('button.cancel.text')}</Button>
          <Button
            variant="contained"
            onClick={handleAddUser}
            disabled={saving || !addUserForm.name || !addUserForm.email || (!addUserSendInvitation && !addUserForm.password)}
          >
            {saving ? t('button.save.loading') : t('button.save.text')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Invite User Dialog */}
      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('accounts.inviteUser')} - {inviteAccount?.name}</DialogTitle>
        <DialogContent>
          <Box display="grid" gap={2} mt={1}>
            <TextField
              label={t('users.email')}
              value={inviteForm.email}
              onChange={e => setInviteForm(prev => ({ ...prev, email: e.target.value }))}
              fullWidth
              autoFocus
            />
            <TextField
              label={t('users.name')}
              value={inviteForm.name}
              onChange={e => setInviteForm(prev => ({ ...prev, name: e.target.value }))}
              fullWidth
              helperText={t('accounts.invitationHelp')}
            />
            <FormControl fullWidth>
              <InputLabel>{t('users.role')}</InputLabel>
              <Select
                value={inviteForm.role}
                label={t('users.role')}
                onChange={e => setInviteForm(prev => ({ ...prev, role: e.target.value }))}
              >
                <MenuItem value="admin">{t('users.roles.admin')}</MenuItem>
                <MenuItem value="user">{t('users.roles.user')}</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)} disabled={saving}>{t('button.cancel.text')}</Button>
          <Button
            variant="contained"
            startIcon={<EmailIcon />}
            onClick={handleInviteUser}
            disabled={saving || !inviteForm.email}
          >
            {saving ? t('button.save.loading') : t('accounts.sendInvitation')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Transfer Ownership Dialog */}
      <Dialog open={transferOpen} onClose={() => setTransferOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('accounts.transferOwnership')} - {transferAccount?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('accounts.transferDescription')}
          </Typography>
          <FormControl fullWidth>
            <InputLabel>{t('accounts.newOwner')}</InputLabel>
            <Select
              value={transferUserId}
              label={t('accounts.newOwner')}
              onChange={e => setTransferUserId(e.target.value)}
            >
              {accountUsers
                .filter(u => u.id !== transferAccount?.ownerId)
                .map(u => (
                  <MenuItem key={u.id} value={u.id}>{u.name} ({u.email})</MenuItem>
                ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTransferOpen(false)} disabled={saving}>{t('button.cancel.text')}</Button>
          <Button variant="contained" color="warning" onClick={handleTransfer} disabled={saving || !transferUserId}>
            {saving ? t('button.save.loading') : t('accounts.transfer')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}

export default AccountsScreen
